/**
 * Insights cart-drawer fallback (v4, 2026-05-16).
 *
 * Previous v3 found .js-drawer-open-cart and called .click() on it,
 * but happygarden's theme doesn't open the drawer in response to a
 * synthetic click event. Likely either:
 *   - The handler is bound to pointerdown/mousedown (not click)
 *   - The drawer is a <cart-drawer> custom element with its own
 *     open()/show() method
 *   - There's a separate <details> / <summary> element that toggles
 *     via the [open] attribute
 *
 * v4 tries every known mechanism in priority order and logs each
 * attempt so Alex can read DevTools and see which fired.
 */
(function () {
  'use strict';

  function log() {
    try {
      var args = ['[insights-cart]'].concat([].slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) {
      /* noop */
    }
  }

  log('script loaded (v4) — waiting for ATC submit');

  function dispatchPointerSequence(el) {
    // Some themes bind to pointerdown/mousedown instead of click.
    // Fire the full sequence so any one of them triggers.
    var rect = el.getBoundingClientRect();
    var x = rect.left + rect.width / 2;
    var y = rect.top + rect.height / 2;
    var common = { bubbles: true, cancelable: true, clientX: x, clientY: y, button: 0 };
    ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'].forEach(function (type) {
      try {
        var EventCtor =
          type.indexOf('pointer') === 0 && typeof PointerEvent !== 'undefined'
            ? PointerEvent
            : MouseEvent;
        el.dispatchEvent(new EventCtor(type, common));
      } catch (e) {
        /* swallow — some themes block synthetic events */
      }
    });
  }

  function openDrawer(item) {
    var detail = { item: item };

    // Dispatch every cart-related event most themes listen for.
    [
      'ajaxProduct:added',
      'cart:open',
      'cart:refresh',
      'cart:added',
      'cart:item-added',
      'cart:build',
      'product:added',
    ].forEach(function (name) {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    });

    // Strategy A — Modern Dawn-style: <cart-drawer> custom element with open()
    var customDrawer = document.querySelector('cart-drawer');
    if (customDrawer && typeof customDrawer.open === 'function') {
      log('opening via <cart-drawer>.open()');
      try {
        customDrawer.open();
        return;
      } catch (e) {
        log('<cart-drawer>.open() threw', e);
      }
    }

    // Strategy B — <details id="CartDrawer"> or <details id="cart-drawer">
    var detailsDrawer = document.querySelector('details#CartDrawer, details#cart-drawer');
    if (detailsDrawer) {
      log('opening <details> drawer by setting [open]');
      detailsDrawer.setAttribute('open', '');
      return;
    }

    // Strategy C — window.theme.cart.open() / window.theme.cartDrawer.open()
    try {
      var t = window.theme;
      if (t) {
        if (t.cart && typeof t.cart.open === 'function') {
          log('opening via window.theme.cart.open()');
          t.cart.open();
          return;
        }
        if (t.cartDrawer && typeof t.cartDrawer.open === 'function') {
          log('opening via window.theme.cartDrawer.open()');
          t.cartDrawer.open();
          return;
        }
      }
    } catch (e) {
      log('window.theme.cart open error', e);
    }

    // Prefer Strategy E (class-toggle) when the drawer DIV exists — happygarden's
    // pattern: <div id="CartDrawer" class="drawer drawer--right">. Strategy E
    // alone opens the drawer; Strategy D's synthetic pointer events trigger a
    // broken theme.js trapFocus call ("Cannot read properties of undefined")
    // on this theme, so we skip them when we know E will handle it.
    var drawerEl = document.querySelector('#CartDrawer, #cart-drawer, .cart-drawer');
    if (drawerEl) {
      setTimeout(function () {
        log('Strategy E — toggling drawer open classes on', drawerEl);
        drawerEl.classList.add('drawer--is-open', 'is-open', 'active');
        drawerEl.removeAttribute('aria-hidden');
        drawerEl.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('js-drawer-open', 'js-drawer-open-cart');
        document.body.classList.add('cart-drawer-open');
      }, 50);
      return;
    }

    // No drawer DIV — fall back to Strategy D's synthetic pointer events
    // on a discoverable trigger. Used when the theme uses a slide-in modal
    // or popup without the classic .drawer container pattern.
    var triggerSelectors = [
      'cart-icon-bubble',
      'a[href="#cart"][data-action]',
      '.js-drawer-open-cart',
      '[data-action="open-cart"]',
      '#CartDrawerToggle',
      '.cart-drawer-trigger',
      'summary[aria-controls*="cart"]',
      'button[aria-controls*="cart" i]',
    ];
    setTimeout(function () {
      for (var i = 0; i < triggerSelectors.length; i++) {
        var trigger = document.querySelector(triggerSelectors[i]);
        if (trigger) {
          log('Strategy D — synthesizing pointer sequence on', triggerSelectors[i], trigger);
          dispatchPointerSequence(trigger);
          try {
            trigger.click();
          } catch (e) {
            /* swallow */
          }
          return;
        }
      }
      // No trigger either — final fallback to /cart navigation
      log('Final fallback — no drawer, no trigger found. Navigating to /cart');
      window.location.href = '/cart';
    }, 100);
  }

  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      log('intercepting ATC submit', form);
      e.preventDefault();
      e.stopImmediatePropagation();

      var formData = new FormData(form);

      // Only a DEFINITE refusal may fall back to the native form. Once the server has accepted the
      // add — or may have (the request failed after leaving) — a second add would double the
      // shopper's quantity, so those paths show the current cart and never submit again.
      function showCart(why, err) {
        log(why + ' — showing the cart, not repeating the add', err);
        window.location.href = '/cart';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      }).then(
        function (res) {
          if (!res.ok) {
            log('cart/add.js refused (' + res.status + ') — falling back to native submit');
            form.submit();
            return;
          }
          // The add is accepted from here: nothing below may reach form.submit().
          return res
            .json()
            .then(
              function (item) {
                return item;
              },
              function (err) {
                log('cart/add.js response unreadable', err);
                return null;
              },
            )
            .then(function (item) {
              log('cart/add.js OK', item && item.title);
              try {
                openDrawer(item);
              } catch (err) {
                showCart('drawer presentation failed after a successful add', err);
              }
            });
        },
        function (err) {
          showCart('cart/add.js request failed, outcome unknown', err);
        },
      );
    },
    true,
  );
})();
